<template>
  <CognitoBrandedShell>
    <div
      v-if="errorMessage"
      class="w-full max-w-md rounded-2xl border border-gray-200 bg-white p-8 text-left shadow-lg sm:text-center"
    >
      <p class="text-lg font-semibold leading-snug text-red-600">
        Could not continue
      </p>
      <p
        class="mt-3 whitespace-pre-line text-base leading-relaxed text-gray-600"
      >
        {{ errorMessage }}
      </p>
      <button
        type="button"
        class="mt-8 w-full rounded-lg bg-[#9489F5] px-4 py-3 text-sm font-medium text-white shadow-sm hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-[#9489F5] focus:ring-offset-2 focus:ring-offset-white"
        @click="returnToHome"
      >
        Return to home
      </button>
    </div>
    <BarLoader
      v-else
      variant="spinner"
      theme="light"
      contained
      message="Getting things ready…"
    />
  </CognitoBrandedShell>
</template>

<script lang="ts">
import { defineComponent, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { cognitoSignUpRedirect } from "@/cognito/auth";
import { useCognitoAuth } from "@/cognito/cognito.config";
import {
  PENDING_SUBSCRIPTION_INVITE_SLUG_KEY,
  PENDING_COMPANY_EMAIL_INVITE_TOKEN_KEY,
} from "@/constants/subscriptionInvite";
import BarLoader from "@/components/shared/BarLoader.vue";
import CognitoBrandedShell from "@/components/cognito/CognitoBrandedShell.vue";

export default defineComponent({
  name: "SubscriptionInviteCognitoLandingView",

  components: { BarLoader, CognitoBrandedShell },

  setup() {
    const route = useRoute();
    const router = useRouter();
    const errorMessage = ref<string | null>(null);

    function returnToHome() {
      router.push({ name: "HomeView" });
    }

    onMounted(async () => {
      sessionStorage.removeItem(PENDING_SUBSCRIPTION_INVITE_SLUG_KEY);

      if (!useCognitoAuth) {
        errorMessage.value =
          "This invite link requires Cognito sign-up. Contact support if this persists.";
        return;
      }

      const slug = (route.params.slug as string)?.trim();
      if (!slug) {
        errorMessage.value =
          "Missing invite link. Use the link from your invitation email.";
        return;
      }

      try {
        sessionStorage.removeItem(PENDING_COMPANY_EMAIL_INVITE_TOKEN_KEY);
        sessionStorage.setItem(PENDING_SUBSCRIPTION_INVITE_SLUG_KEY, slug);
        await cognitoSignUpRedirect();
      } catch (e) {
        sessionStorage.removeItem(PENDING_SUBSCRIPTION_INVITE_SLUG_KEY);
        errorMessage.value =
          e instanceof Error
            ? e.message
            : "Could not start sign-up. Try again.";
      }
    });

    return { errorMessage, returnToHome };
  },
});
</script>
